import { Tab } from '@mui/material';
import { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader, QueryTabs, QueryTabPanel } from 'components';
import { OrdersTab } from 'enums';
import { MerchantCreateOrderDialog } from 'features/payout-orders';
import { useMeta, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { MerchantActiveOrders } from './ActiveOrders';
import { MerchantAllOrders } from './AllOrders';
import { MerchantCancelledOrders } from './CancelledOrders';
import { MerchantCompletedOrders } from './CompletedOrders';
import { MerchantDisputeOrders } from './DisputeOrders';

export const MerchantPayoutOrdersPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const { isMerchantCanCreatePayoutOrderEnabled } = useMeta();
  const { isMerchantOperator } = useUser();
  const canCreatePayoutOrder =
    isMerchantCanCreatePayoutOrderEnabled && !isMerchantOperator;

  const handleCreate = useCallback(() => setCreateOrderDialogOpen(true), []);

  return (
    <Fragment>
      <PageHeader
        title={t('title')}
        divider={false}
        {...(canCreatePayoutOrder && {
          rightContentButton: { onClick: handleCreate },
        })}
      />
      <QueryTabs
        tabs={[
          OrdersTab.Active,
          OrdersTab.Completed,
          OrdersTab.Cancelled,
          OrdersTab.Dispute,
          OrdersTab.All,
        ]}
      >
        <Tab value={OrdersTab.Active} label={t('tabs.active')} />
        <Tab value={OrdersTab.Completed} label={t('tabs.completed')} />
        <Tab value={OrdersTab.Cancelled} label={t('tabs.cancelled')} />
        <Tab value={OrdersTab.Dispute} label={t('tabs.dispute')} />
        <Tab value={OrdersTab.All} label={t('tabs.all')} />
      </QueryTabs>
      <QueryTabPanel value={OrdersTab.Active}>
        <MerchantActiveOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Completed}>
        <MerchantCompletedOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Cancelled}>
        <MerchantCancelledOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Dispute}>
        <MerchantDisputeOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.All}>
        <MerchantAllOrders />
      </QueryTabPanel>
      {canCreatePayoutOrder && (
        <MerchantCreateOrderDialog
          open={createOrderDialogOpen}
          onClose={() => setCreateOrderDialogOpen(false)}
        />
      )}
    </Fragment>
  );
};
